import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { validateField, sanitizeString, isSecureInput, errorMessages } from '../lib/validation';

const LOGO_URL = "/logo.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    password: ''
  });
  
  // Track touched fields
  const [touched, setTouched] = useState({
    email: false,
    password: false
  });

  const redirect = searchParams.get('redirect') || '/';

  // Validate field on blur or change
  const validateFieldOnChange = (field, value) => {
    let error = '';
    
    // Security check
    if (!isSecureInput(value)) {
      error = errorMessages.security;
    } else {
      switch (field) {
        case 'email':
          error = validateField(value, { required: true, email: true });
          break;
        case 'password':
          error = validateField(value, { required: true, minLength: 6 });
          break;
        default:
          break;
      }
    }
    
    setFieldErrors(prev => ({ ...prev, [field]: error || '' }));
    return !error;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const value = field === 'email' ? email : password;
    validateFieldOnChange(field, value);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) {
      validateFieldOnChange('email', value);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) {
      validateFieldOnChange('password', value);
    }
  };

  const validateForm = () => {
    const emailValid = validateFieldOnChange('email', email);
    const passwordValid = validateFieldOnChange('password', password);
    setTouched({ email: true, password: true });
    return emailValid && passwordValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }

    // Sanitize inputs
    const sanitizedEmail = sanitizeString(email.toLowerCase());
    const sanitizedPassword = password; // Don't sanitize password as it may contain special chars

    setLoading(true);

    try {
      const userData = await login(sanitizedEmail, sanitizedPassword);
      if (userData.role === 'admin' && redirect === '/') {
        navigate('/admin');
      } else {
        navigate(redirect);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#FAF7F2] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <Link to="/">
              <img
                src={LOGO_URL}
                alt="La Finca"
                className="h-24 mx-auto mb-4"
              />
            </Link>
            <h1 className="font-serif text-2xl text-[#1A1A1A]">Iniciar Sesión</h1>
            <p className="text-[#1A1A1A]/60 mt-2">
              Bienvenido de vuelta a La Finca
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <Label htmlFor="email" className="text-[#5C4033]">Correo electrónico</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C8962D]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => handleBlur('email')}
                  placeholder="tu@email.com"
                  className={`pl-10 ${touched.email && fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>
              {touched.email && fieldErrors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {fieldErrors.email}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-[#5C4033]">Contraseña</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C8962D]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => handleBlur('password')}
                  placeholder="••••••••"
                  className={`pl-10 pr-10 ${touched.password && fieldErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {touched.password && fieldErrors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#5C4033] hover:bg-[#3D5A3E] text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#1A1A1A]/60">
              ¿No tienes cuenta?{' '}
              <Link
                to={`/registro${redirect !== '/' ? `?redirect=${redirect}` : ''}`}
                className="text-[#5C4033] hover:text-[#C8962D] font-medium"
              >
                Regístrate
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
